import { useEffect, useMemo, useRef, useState } from "react";
import { AlertTriangle, Search, X } from "lucide-react";
import { api, searchProducts, getStock, listDiscountRules, NO_STOCK_DATA_TEXT } from "@/services/api";
import { isRuleExpired, evaluateBrandLock, brandLockViolationLevel, BrandRuleList } from "@/components/BrandLockTable";
import { MaterialPickerTable } from "@/components/MaterialPickerTable";
import { derivedOptions } from "@/lib/filterOptions";
import type { BrandCheck, StockInfo } from "@/components/MaterialPickerTable";
import { stockStateOf } from "@/components/MaterialTable";
import type { AddMaterialsResult } from "@/services/api";
import type { Quote, CatalogProduct, OemDiscountRule } from "@/types";
import { colors, semanticColors, radius, spacing, heights, layout, typography, iconSize, iconStroke, shadows, overlayMaxWidth } from "@/styles/tokens";

// Brand and category options are DERIVED from the loaded rows, exactly as
// /catalog derives them (configurable identity P1-9): the names are master
// data, so a compiled list goes stale in the silent direction.

const SELECT_STYLE: React.CSSProperties = {
  height: heights.input, padding: `0 ${spacing[8]}px`, fontSize: typography.fontSize.base,
  color: semanticColors.textPrimary, backgroundColor: semanticColors.surface,
  border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
  outline: "none",
};

function ToggleBtn({
  active, onClick, children, testid, disabled = false, title,
}: { active: boolean; onClick: () => void; children: React.ReactNode; testid: string; disabled?: boolean; title?: string }) {
  return (
    <button
      data-testid={testid}
      data-active={active ? "true" : "false"}
      onClick={onClick}
      disabled={disabled}
      title={title}
      style={{
        opacity: disabled ? 0.5 : 1,
        height: heights.input, padding: `0 ${spacing[10]}px`,
        fontSize: typography.fontSize.sm, fontWeight: typography.fontWeight.medium,
        border: `1px solid ${active ? colors.indigo600 : semanticColors.border}`,
        borderRadius: radius.base,
        backgroundColor: active ? colors.indigo600 : colors.white,
        color: active ? semanticColors.onAccent : semanticColors.textSecondary,
        cursor: disabled ? "not-allowed" : "pointer", whiteSpace: "nowrap",
      }}
    >
      {children}
    </button>
  );
}

// Large right-anchored slide-over — a third overlay layout family alongside
// DetailPanel.tsx's inline aside and NewQuoteModal's centered dialog. See
// CLAUDE.md "Slide-overs". Hand-rolled like NewQuoteModal, for the same
// reason: neither existing shell fits (DetailPanel is a fixed 420px inline
// aside; the modal is a centered, rounded 480px box).
export function MaterialPicker({
  quote,
  storeKey,
  onClose,
  onAdded,
}: {
  quote: Quote;
  // ⚠️ WHICH BUCKET THE PICKS ARE WRITTEN TO, decided by the caller.
  //
  // This used to be `quote.quoteNo`, read off the quote below. Quote NAMES ARE
  // NOT UNIQUE -- live holds Q-2026-0001 seven times and the mock quotes reuse
  // the same names -- so a reopened quote landed in whichever mock quote's
  // bucket shared its name, and since that bucket was never seeded, the add
  // fell through to the 180-row mockMaterials array. QuoteDetail derives the
  // key once (savedQuoteStoreKey(detail.quote_id) when reopened, the quoteNo
  // otherwise) and every writer on that screen uses the one value.
  //
  // `quote` is still here, but for DISPLAY and RULE MATCHING only -- the
  // endClient header and the brand-lock banner. It is no longer a store key.
  storeKey: string;
  onClose: () => void;
  onAdded: (result: AddMaterialsResult) => void;
}) {
  // ---- The real product master -------------------------------------------
  //
  // ⚠️ NO MOCK FALLBACK ANYWHERE ON THIS PATH. On any failure the rows are
  // CLEARED and an error is shown. Falling back to mockProducts would put
  // invented parts, with invented prices, behind a control that looks like it
  // is showing the real catalog -- and a pick from it would then be saved
  // against a product id that does not exist. Two catalogs live at once
  // behind one control is the same class of bug the two-keys-live cutover
  // existed to kill.
  const [products, setProducts] = useState<CatalogProduct[]>([]);
  const [catalogStatus, setCatalogStatus] = useState<"loading" | "ok" | "error">("loading");
  const [catalogError, setCatalogError] = useState("");
  // Bumped by Retry. A separate counter rather than re-setting `search` to
  // its current value: React bails out of a state update that is Object.is-
  // equal, so setSearch(q => q) would not re-run the fetch effect at all and
  // the button would silently do nothing.
  const [retryTick, setRetryTick] = useState(0);

  const allBrandLockRules = useMemo(() => api.getBrandLockRules(), []);

  // ---- Stock levels, for the rows currently on screen ---------------------
  //
  // ⚠️ FAIL SOFT. This is the OPPOSITE of the catalog rule directly above,
  // and the difference is deliberate: the catalog IS the picker -- a wrong or
  // invented product gets quoted and saved against an id that does not exist,
  // so a failed catalog read clears the rows and refuses to draw the table.
  // Stock is an annotation ON those rows. A failed stock read leaves every
  // product pickable with its level UNKNOWN, which is exactly what is true,
  // and blocking the picker over it would stop someone quoting a part they
  // can see in front of them because a cache was cold.
  //
  // `null` therefore means UNKNOWN-EVERYWHERE and is the state this holds
  // before the first read, during every read, and after a failed one. It is
  // never an empty Map standing in for "we checked and there is none".
  const [stockLevels, setStockLevels] = useState<Map<number, number> | null>(null);
  // ⚠️ THE DISCOUNT RULES, LOADED WHEN THE PICKER OPENS AND HELD NOWHERE ELSE.
  //
  // Read here rather than cached in services/api.ts on purpose. A module-level
  // rule cache would go stale the moment an Admin saved a rate, and nothing
  // would invalidate it -- so a line added a minute later would resolve
  // against a rule that no longer exists, silently. Fetching on open means the
  // rules a line is created under are the ones that existed when the user
  // opened the picker, and a rate saved before that is resolvable immediately.
  //
  // ⚠️ null IS "NOT LOADED", NOT "NO RULES". They are handed to
  // addMaterialsToQuote as `?? []`, and an empty list resolves to a REFUSAL
  // rather than to 0% -- which is the correct answer when this read failed:
  // we do not know what this customer pays, so the line has no cost basis.
  const [discountRules, setDiscountRules] = useState<OemDiscountRule[] | null>(null);
  const [stockError, setStockError] = useState("");

  const clientRules = useMemo(
    () => allBrandLockRules.filter((r) => r.endClient === quote.endClient && !isRuleExpired(r)),
    [allBrandLockRules, quote.endClient]
  );
  const hasStrictLock = useMemo(() => clientRules.some((r) => r.lockType === "Strict"), [clientRules]);

  const [search, setSearch] = useState("");
  // The query the CURRENT rows were fetched for, so the no-match message can
  // quote what was actually searched rather than what is in the box this
  // instant (they differ while a keystroke is still debouncing).
  const [searchedFor, setSearchedFor] = useState("");
  const [brand, setBrand] = useState("All");
  const [category, setCategory] = useState("All");
  // The CURRENT choice stays listed even when a new search returns no row
  // with it, so the select never shows a value it does not offer.
  const brandOpts = useMemo(() => {
    const d = derivedOptions(products, (p) => p.brand);
    return ["All", ...(brand !== "All" && !d.includes(brand) ? [...d, brand] : d)];
  }, [products, brand]);
  const categoryOpts = useMemo(() => {
    const d = derivedOptions(products, (p) => p.category);
    return ["All", ...(category !== "All" && !d.includes(category) ? [...d, category] : d)];
  }, [products, category]);
  const [inStockOnly, setInStockOnly] = useState(false);
  // no-mock-stock-on-live: no real stock source is connected, so there is
  // nothing to filter on and the stock column says so.
  const [noStockSource, setNoStockSource] = useState(false);
  const [allowedBrandsOnly, setAllowedBrandsOnly] = useState(hasStrictLock);
  const [selection, setSelection] = useState<Record<string, number>>({});

  useEffect(() => {
    const onKeyDown = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [onClose]);

  // ---- Fetching ------------------------------------------------------------
  //
  // THE QUERY GOES TO THE SERVER; the other four filters stay client-side.
  // The endpoint searches part_number/description (both indexed) via ?q= and
  // has no filter for brand, category, stock or brand-locks -- so sending it
  // anything else would be inventing a parameter, and filtering the query
  // locally would mean paging the whole catalog to the browser to do work
  // Postgres already does.
  //
  // Debounced, because this fires per keystroke. 250ms is short enough to
  // feel immediate and long enough that typing a part number is one request
  // rather than seven.
  //
  // ⚠️ LAST-RESPONSE-WINS, guarded by a token rather than by cancellation.
  // Two searches in flight can land out of order, and without this the slower
  // earlier one overwrites the newer rows -- the list would show results for
  // a query the box no longer contains. `fetchToken` is compared on arrival
  // and a stale answer is dropped whole, error included: reporting a failure
  // for a query nobody is waiting on any more is its own kind of wrong.
  const fetchToken = useRef(0);
  useEffect(() => {
    const token = ++fetchToken.current;
    const query = search.trim();
    setCatalogStatus("loading");

    const timer = setTimeout(async () => {
      const result = await searchProducts(query);
      if (fetchToken.current !== token) return;

      if (!result.ok || !result.products) {
        // CLEARED, not left standing. Stale rows under an error message read
        // as current results and would be picked from.
        setProducts([]);
        setCatalogError(result.error ?? "The product catalog could not be loaded.");
        setCatalogStatus("error");
        return;
      }
      setProducts(result.products);
      setSearchedFor(query);
      setCatalogError("");
      setCatalogStatus("ok");
    }, 250);

    return () => clearTimeout(timer);
  }, [search, retryTick]);

  // ---- The stock read -----------------------------------------------------
  //
  // Fires for whatever the catalog just returned, so it asks about exactly
  // the ids on screen rather than the whole master. Same last-response-wins
  // token discipline as the catalog fetch above and for the same reason: two
  // searches in flight can land out of order, and stale levels painted onto
  // the newer rows would be wrong per row rather than merely late.
  //
  // ⚠️ NO `warehouse` HERE. The aggregate is a SUM across locations, so there
  // is no single place to name; ?locations=1 is not requested because the
  // picker shows one figure per product and naming one of several warehouses
  // beside a total would attribute all of it to part of it.
  const stockToken = useRef(0);
  useEffect(() => {
    const token = ++stockToken.current;
    const ids = products.map((p) => p.id);

    // Nothing on screen: clear to unknown without a request. getStock guards
    // this too (an empty list is not a request for the whole catalog), but
    // not firing at all is clearer than firing and discarding.
    if (ids.length === 0) {
      setStockLevels(null);
      setStockError("");
      return;
    }

    let cancelled = false;
    (async () => {
      const result = await getStock(ids);
      if (cancelled || stockToken.current !== token) return;

      if (!result.ok || !result.stock) {
        // ⚠️ UNKNOWN, NOT EMPTY, AND THE ROWS STAY. `null` rather than
        // `new Map()`: an empty map would say every product was checked and
        // found to have none, which is a claim nobody made.
        setStockLevels(null);
        setStockError(result.error ?? "Stock levels could not be read.");
        return;
      }
      setStockLevels(result.stock);
      setNoStockSource(result.configured === false);
      if (result.configured === false) setInStockOnly(false);
      setStockError("");
    })();

    return () => { cancelled = true; };
  }, [products]);

  // ⚠️ THREE STATES OUT, AND A MISS IS `undefined` NOT 0. `.has` before
  // `.get` is what separates "the server reported 0" from "the server did not
  // mention this product"; `stockLevels?.get(id)` alone would be correct
  // today only by accident of Map returning undefined, and would break the
  // moment anyone "helpfully" added a `?? 0`.
  const stockOf = (productId: number): StockInfo => {
    if (!stockLevels || !stockLevels.has(productId)) return { available: undefined };
    return { available: stockLevels.get(productId) };
  };

  const brandCheck = (p: CatalogProduct): BrandCheck => {
    // ⚠️ AN UNCLASSIFIED PRODUCT IS REFUSED, NOT WAVED THROUGH.
    //
    // brand and category are the join keys a brand lock is evaluated on. A
    // product missing either -- because nobody classified it in Odoo, or
    // because it carries a brand name this build's ProductBrand union does
    // not contain -- matches no rule at all, so evaluateBrandLock returns
    // "no lock applies" and the row becomes freely pickable. That is the
    // silent direction: a client's Strict lock quietly stops firing on
    // exactly the products least likely to have been checked by hand.
    //
    // Hiding the row instead would be the other silent failure -- a product
    // that exists in the catalog and cannot be found in the picker, with
    // nothing saying why. So it is shown, refused, and explained.
    if (!p.brand || !p.category) {
      const missing = !p.brand && !p.category
        ? "a brand or a category"
        : !p.brand ? "a brand" : "a category";
      return {
        level: "unclassified",
        tip: `Unclassified — this product has no ${missing} recorded, so this `
           + `client's brand-lock rules cannot be checked against it. Classify `
           + `it in Odoo before quoting it.`,
      };
    }

    const evaluation = evaluateBrandLock(clientRules, p.category);
    const level = brandLockViolationLevel(evaluation, p.brand);
    if (level === null) return { level: null, tip: "" };
    const allowed = evaluation.allowedBrands.join(" or ");
    const tip = level === "strict"
      ? `Brand locked. This client requires ${allowed} for ${p.category}.`
      : `Preferred brand rule. This client prefers ${allowed} for ${p.category}.`;
    return { level, tip };
  };

  // THE FOUR CLIENT-SIDE FILTERS. The text query is NOT among them any more --
  // the endpoint did that, against indexed columns, before these rows arrived.
  // Re-filtering on it here would silently hide rows the server matched on a
  // field this list does not search (description casing, say).
  const filtered = useMemo(() => {
    return products.filter((p) => {
      if (brand !== "All" && p.brand !== brand) return false;
      if (category !== "All" && p.category !== category) return false;
      // HIDE-UNKNOWN, matching MaterialTable's copy of this toggle. The
      // filter asks for products KNOWN to be in stock, so an unreported one
      // is hidden -- but it is hidden for want of evidence, not because it is
      // being called empty. The old `available <= 0` could not tell the two
      // apart because a miss had already become 0 before it got here.
      if (inStockOnly && stockStateOf(stockOf(p.id).available) !== "in") return false;
      if (allowedBrandsOnly && brandCheck(p).level !== null) return false;
      return true;
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [products, brand, category, inStockOnly, allowedBrandsOnly, clientRules, stockLevels]);

  // ---- Why the table is empty ----------------------------------------------
  //
  // ⚠️ AN EMPTY PICKER MUST NEVER READ AS A BROKEN PICKER, and with the real
  // catalog this is the COMMON case, not the edge one: it holds a handful of
  // products, so most searches legitimately match nothing.
  //
  // ⚠️ IT MUST ALSO NAME THE FILTER WHEN A FILTER IS THE CAUSE. Two toggles
  // can empty the list for reasons that have nothing to do with the catalog:
  //
  //   * "In stock only" hides every product whose level is UNKNOWN as well as
  //     every one reported at zero, so a cold cache or a failed stock read
  //     empties it while the catalog itself is perfectly healthy. (It read
  //     mock inventory joined on PART NUMBER until slice 2.0b-ii, which hid
  //     everything off the real master unconditionally.)
  //   * "Allowed brands only" defaults ON whenever the client has a Strict
  //     lock, so it can be filtering before anyone has touched it. The control
  //     is absent when there is no authoritative rule for this client.
  //
  // Saying "No matching products" in either case sends someone looking for a
  // catalog problem that does not exist.
  const emptyMessage = useMemo(() => {
    if (products.length === 0) {
      return searchedFor
        ? `No product matches “${searchedFor}”.`
        : "The product catalog is empty.";
    }
    // Rows came back and the filters removed all of them. Name them in the
    // order they sit in the filter bar.
    const culprits = [
      brand !== "All" ? `Brand: ${brand}` : null,
      category !== "All" ? `Category: ${category}` : null,
      inStockOnly ? "“In stock only”" : null,
      allowedBrandsOnly ? "“Allowed brands only”" : null,
    ].filter(Boolean) as string[];

    if (culprits.length === 0) return "No matching products.";
    const list = culprits.length === 1
      ? culprits[0]
      : `${culprits.slice(0, -1).join(", ")} and ${culprits[culprits.length - 1]}`;
    return `${products.length} ${products.length === 1 ? "product" : "products"} found, `
         + `but ${list} ${culprits.length === 1 ? "hides" : "hide"} `
         + `${products.length === 1 ? "it" : "them all"}.`;
  }, [products.length, searchedFor, brand, category, inStockOnly, allowedBrandsOnly]);

  // ⚠️ SELECTION IS KEYED BY CATALOG ID, NOT PART NUMBER. Part numbers are not
  // unique -- the backend has no constraint saying they are, deliberately,
  // because two manufacturers really do ship different goods under one string
  // -- so keying on partNo would collapse two distinct products into a single
  // pick and add their quantities together.
  const toggle = (p: CatalogProduct) => {
    const level = brandCheck(p).level;
    if (level === "strict" || level === "unclassified") return;
    const key = String(p.id);
    setSelection((prev) => {
      const next = { ...prev };
      if (key in next) delete next[key];
      else next[key] = 1;
      return next;
    });
  };

  const setQty = (productId: number, qty: number) => {
    const key = String(productId);
    setSelection((prev) => (key in prev ? { ...prev, [key]: qty } : prev));
  };

  // The picked rows, resolved back to the catalog objects they came from.
  //
  // ⚠️ A SELECTION WHOSE ROW IS NO LONGER LOADED IS DROPPED. `products` is
  // replaced on every search, so a product picked under one query is not in
  // the list under the next. Resolving here (rather than storing the objects
  // in `selection`) keeps one source of truth, and the filter means a pick
  // can never outlive the row that justified it -- the count and the Add
  // button always describe rows that are actually on screen.
  const selectedProducts = useMemo(() => {
    const byId = new Map(products.map((p) => [String(p.id), p]));
    return Object.entries(selection)
      .map(([key, qty]) => ({ product: byId.get(key), qty }))
      .filter((e): e is { product: CatalogProduct; qty: number } => e.product !== undefined);
  }, [products, selection]);

  const selectedCount = selectedProducts.length;
  // Counts the selected lines that will be PARTLY OR WHOLLY sourced in.
  // Suppressed for unknown (no shortfall can be claimed against a level
  // nobody reported) and counted for a stated 0, where the entire quantity is
  // sourced in -- the strongest case there is, and the one the old
  // `available > 0` guard silently dropped.
  const partlyFromStock = selectedProducts.filter(({ product, qty }) => {
    const { available } = stockOf(product.id);
    return stockStateOf(available) !== "unknown" && qty > available!;
  }).length;

  // One read, on open. No refresh button and no polling: a picker session is
  // short, and re-reading mid-session would let two picks in one sitting
  // resolve against two different rate cards.
  useEffect(() => {
    let cancelled = false;
    (async () => {
      const result = await listDiscountRules();
      if (cancelled) return;
      // A failure leaves this null. See the state comment: null becomes [],
      // which refuses rather than defaulting, and the refusal names both
      // remedies. Not surfaced as a banner here because the picker's job is
      // choosing products -- the refusal reaches the user on the line itself,
      // where the remedy is.
      setDiscountRules(result.ok ? (result.rules ?? []) : null);
    })();
    return () => { cancelled = true; };
  }, []);

  const summaryText = selectedCount === 0
    ? "0 items selected"
    : `${selectedCount} ${selectedCount === 1 ? "item" : "items"} selected`
      + (partlyFromStock > 0 ? ` · ${partlyFromStock} partly from stock` : "");

  const handleAdd = () => {
    if (selectedCount === 0) return;
    // THE RESOLVED ROW TRAVELS WITH THE PICK. addMaterialsToQuote used to
    // re-resolve a part number against mockProducts, which silently dropped
    // every real product -- see MaterialPick.product.
    const picks = selectedProducts.map(({ product, qty }) => {
      const level = brandCheck(product).level;
      return {
        product,
        qty,
        lockWarning: level === "preferred" ? ("preferred" as const) : undefined,
      };
    });
    // ⚠️ THE LEVELS THE USER WAS LOOKING AT travel with the picks, rather
    // than addMaterialsToQuote re-reading them. Re-reading would open a
    // window where the pill said one thing and the created line recorded
    // another. `null` (unknown-everywhere, including after a failed read)
    // passes through as undefined, so every new line is created UNKNOWN --
    // never zeroed.
    // ⚠️ THE PARTNER ID, NOT THE NAME. quote.endClient is a display string and
    // matching a rate on it is exactly what the mock resolver did. The entity
    // id is the res.partner this quote is FOR, and it is what
    // anixify.discount.rule keys on. A quote with no customer chosen yet has
    // none, and the resolver treats that as PENDING rather than as a refusal.
    const result = api.addMaterialsToQuote(storeKey, picks, stockLevels ?? undefined, {
      rules: discountRules ?? [],
      partnerId: quote.customer?.entity?.id,
      partnerName: quote.customer?.entity?.name ?? quote.endClient,
    });
    onClose();
    onAdded(result);
  };

  const strictCount = clientRules.filter((r) => r.lockType === "Strict").length;
  const preferredCount = clientRules.filter((r) => r.lockType === "Preferred").length;
  const ruleSummary = clientRules.length === 0
    ? "Brand locks not configured"
    : [
        strictCount > 0 ? `${strictCount} Strict` : null,
        preferredCount > 0 ? `${preferredCount} Preferred` : null,
      ].filter(Boolean).join(", ") + (clientRules.length === 1 ? " rule" : " rules");

  return (
    <div
      data-testid="material-picker-backdrop"
      onClick={onClose}
      style={{
        position: "fixed", top: heights.topBar, left: 0, right: 0, bottom: 0, backgroundColor: semanticColors.overlay,
        display: "flex", justifyContent: "flex-end", zIndex: 1000,
      }}
    >
      <div
        data-testid="material-picker"
        onClick={(e) => e.stopPropagation()}
        style={{
          width: layout.pickerWidth,
          // Inert at any width >= 736px -- see overlayMaxWidth. Without it the
          // slide-over's own close X sits off the right edge of a phone.
          maxWidth: overlayMaxWidth,
          height: "100%",
          backgroundColor: semanticColors.surface,
          borderLeft: `1px solid ${semanticColors.border}`,
          boxShadow: shadows.modal,
          display: "flex", flexDirection: "column", overflow: "hidden",
        }}
      >
        {/* Header */}
        <div
          className="flex items-start justify-between"
          style={{ padding: `${spacing[16]}px ${spacing[20]}px`, borderBottom: `1px solid ${semanticColors.border}`, flexShrink: 0 }}
        >
          <div>
            <div
              data-testid="material-picker-title"
              style={{
                fontSize: typography.fontSize.lg, fontWeight: typography.fontWeight.semibold, color: semanticColors.textPrimary,
                letterSpacing: typography.letterSpacing.tight,
              }}
            >
              Add Materials
            </div>
            <div
              data-testid="material-picker-subtitle"
              style={{ fontSize: typography.fontSize.sm, color: semanticColors.textMuted, marginTop: spacing[2] }}
            >
              {quote.endClient} · {ruleSummary}
            </div>
          </div>
          <button
            data-testid="material-picker-close"
            onClick={onClose}
            title="Close"
            style={{
              width: heights.iconButton, height: heights.iconButton, padding: 0, cursor: "pointer",
              background: "transparent", border: "none", color: semanticColors.textSecondary,
              display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0,
            }}
          >
            <X size={iconSize.lg} strokeWidth={iconStroke.regular} />
          </button>
        </div>

        {/* Brand rule banner */}
        <div
          data-testid="material-picker-brand-banner"
          style={{
            padding: `${spacing[10]}px ${spacing[20]}px`,
            backgroundColor: semanticColors.panelBg, borderBottom: `1px solid ${semanticColors.border}`,
            flexShrink: 0,
          }}
        >
          <BrandRuleList rules={clientRules} testIdPrefix="picker-brand" />
        </div>

        {/* Body */}
        <div style={{ flex: 1, minHeight: 0, display: "flex", flexDirection: "column", padding: `${spacing[16]}px ${spacing[20]}px 0` }}>
          {/* Filter bar */}
          <div
            data-testid="material-picker-filter-bar"
            className="flex items-center"
            style={{
              minHeight: heights.filterBar, padding: `0 ${spacing[12]}px`, marginBottom: spacing[12],
              backgroundColor: semanticColors.panelBg,
              border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
              gap: spacing[12], flexWrap: "wrap", flexShrink: 0,
            }}
          >
            <div
              className="flex items-center"
              style={{
                flex: 1, minWidth: layout.searchMinWidth,
                height: heights.input, padding: `0 ${spacing[8]}px`,
                backgroundColor: semanticColors.surface,
                border: `1px solid ${semanticColors.border}`, borderRadius: radius.base,
                gap: spacing[6],
              }}
            >
              <Search size={iconSize.md} strokeWidth={iconStroke.regular} color={semanticColors.textPlaceholder} />
              <input
                data-testid="material-picker-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Search part number or description"
                style={{
                  flex: 1, minWidth: 0, height: "100%",
                  fontSize: typography.fontSize.base, color: semanticColors.textPrimary,
                  background: "transparent", border: "none", outline: "none",
                }}
              />
            </div>
            <select data-testid="material-picker-filter-brand" value={brand}
                    onChange={(e) => setBrand(e.target.value)} style={SELECT_STYLE}>
              {brandOpts.map((b) => <option key={b} value={b}>{b === "All" ? "Brand: All" : b}</option>)}
            </select>
            <select data-testid="material-picker-filter-category" value={category}
                    onChange={(e) => setCategory(e.target.value)} style={SELECT_STYLE}>
              {categoryOpts.map((c) => <option key={c} value={c}>{c === "All" ? "Category: All" : c}</option>)}
            </select>
            <ToggleBtn testid="material-picker-toggle-in-stock" active={inStockOnly && !noStockSource}
                       disabled={noStockSource}
                       title={noStockSource ? `${NO_STOCK_DATA_TEXT}: no stock source is connected.` : undefined}
                       onClick={() => { if (!noStockSource) setInStockOnly((v) => !v); }}>
              In stock only
            </ToggleBtn>
            {clientRules.length > 0 && (
              <ToggleBtn testid="material-picker-toggle-allowed-brands" active={allowedBrandsOnly} onClick={() => setAllowedBrandsOnly((v) => !v)}>
                Allowed brands only
              </ToggleBtn>
            )}
          </div>

          {/* Count. Singular matters here: the real catalog is small, so
              "1 of 1 products" is a sentence this actually renders. */}
          <div style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary, marginBottom: spacing[8], flexShrink: 0 }}>
            <span data-testid="material-picker-filtered-count" className="tabular-nums">{filtered.length}</span>
            {filtered.length !== products.length ? (
              <> of <span className="tabular-nums">{products.length}</span>{products.length === 1 ? " product" : " products"}</>
            ) : (
              <>{filtered.length === 1 ? " product" : " products"}</>
            )}
          </div>

          {/* Table — the only part of the picker that scrolls.
              THREE STATES, and the error one is NOT an empty table: an empty
              grid under a filter bar reads as "nothing matched", which is a
              statement about the catalog rather than about the request that
              failed. */}
          <div style={{ flex: 1, minHeight: 0, paddingBottom: spacing[16] }}>
            {catalogStatus === "loading" ? (
              <div
                data-testid="material-picker-loading"
                style={{
                  padding: spacing[40], textAlign: "center",
                  fontSize: typography.fontSize.base, color: semanticColors.textPlaceholder,
                }}
              >
                Loading catalog…
              </div>
            ) : catalogStatus === "error" ? (
              <div
                data-testid="material-picker-error"
                className="flex items-start"
                style={{
                  gap: spacing[8], padding: `${spacing[10]}px ${spacing[12]}px`,
                  backgroundColor: semanticColors.destructiveBg,
                  border: `1px solid ${semanticColors.destructiveBorder}`,
                  borderRadius: radius.base,
                  fontSize: typography.fontSize.sm, color: semanticColors.destructive,
                }}
              >
                <AlertTriangle
                  size={iconSize.md}
                  strokeWidth={iconStroke.regular}
                  style={{ flexShrink: 0, marginTop: spacing[2] }}
                />
                <span>
                  <strong>The product catalog could not be loaded.</strong>{" "}
                  <span data-testid="material-picker-error-detail">{catalogError}</span>{" "}
                  {/* No mock fallback and no "showing cached results" -- there
                      are none. Retrying is the only honest offer. */}
                  <button
                    data-testid="material-picker-retry"
                    onClick={() => setRetryTick((n) => n + 1)}
                    style={{
                      background: "none", border: "none", padding: 0,
                      color: semanticColors.destructive, textDecoration: "underline",
                      cursor: "pointer", font: "inherit",
                    }}
                  >
                    Retry
                  </button>
                </span>
              </div>
            ) : (
              <MaterialPickerTable
                rows={filtered}
                height="100%"
                brandCheck={brandCheck}
                stockOf={stockOf}
                noStockSource={noStockSource}
                selection={selection}
                onToggle={toggle}
                onQtyChange={setQty}
                emptyMessage={emptyMessage}
              />
            )}
          </div>

          {/* ⚠️ A NOTE, NOT AN ERROR BAR, AND IT SITS BELOW THE TABLE RATHER
              THAN REPLACING IT. The catalog's failure banner above is an
              alternative TO the table because a picker with no products is
              not usable; this one accompanies a fully working picker whose
              stock column happens to say nothing. Muted, no Retry, nothing
              blocked -- reload or search again and it re-reads. */}
          {!!stockError && catalogStatus === "ok" && (
            <div
              data-testid="material-picker-stock-error"
              style={{
                marginTop: spacing[8], fontSize: typography.fontSize.xs,
                color: semanticColors.textMuted,
              }}
            >
              Stock levels are unavailable, so every level reads as unknown. {stockError}
            </div>
          )}
        </div>

        {/* Footer */}
        <div
          className="flex items-center justify-between"
          style={{ borderTop: `1px solid ${semanticColors.border}`, padding: spacing[16], flexShrink: 0 }}
        >
          <div data-testid="material-picker-summary" style={{ fontSize: typography.fontSize.sm, color: semanticColors.textSecondary }}>
            {summaryText}
          </div>
          <div className="flex items-center" style={{ gap: spacing[8] }}>
            <button
              data-testid="material-picker-cancel"
              className="btn-secondary"
              onClick={onClose}
              style={{
                height: heights.control, padding: `0 ${spacing[14]}px`, backgroundColor: semanticColors.surface,
                color: semanticColors.textSecondary, border: `1px solid ${semanticColors.border}`,
                borderRadius: radius.base, fontSize: typography.fontSize.base, cursor: "pointer",
              }}
            >
              Cancel
            </button>
            <button
              data-testid="material-picker-add"
              className="btn-primary"
              onClick={handleAdd}
              disabled={selectedCount === 0}
              style={{
                height: heights.control, padding: `0 ${spacing[14]}px`,
                backgroundColor: selectedCount > 0 ? colors.indigo600 : semanticColors.textDisabled,
                color: semanticColors.onAccent, border: "none", borderRadius: radius.base,
                fontSize: typography.fontSize.base, fontWeight: typography.fontWeight.medium,
                cursor: selectedCount > 0 ? "pointer" : "not-allowed",
              }}
            >
              Add to Quote
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
